"use client";

import { useEffect, useMemo, useState } from "react";
import {
  Users,
  Plus,
  Mail,
  Settings,
  Trash2,
  Loader2,
  Check,
  X,
} from "lucide-react";

/* ================= CONFIG ================= */

const API_BASE =
  (process.env.NEXT_PUBLIC_API_URL).replace(/\/$/, "");

/* ================= TYPES ================= */

type Team = {
  id: number;
  name: string;
};

type Member = {
  id: number;
  email: string;
  role: "owner" | "admin" | "member";
};

type Permissions = Record<string, boolean>;

/* ================= PAGE ================= */

export default function TeamsPage() {
  /* ---------- DATA ---------- */
  const [teams, setTeams] = useState<Team[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [activeTeam, setActiveTeam] = useState<number | null>(null);

  /* ---------- STATE ---------- */
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  /* ---------- UI ---------- */
  const [showCreate, setShowCreate] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const [newTeamName, setNewTeamName] = useState("");
  const [renameTeam, setRenameTeam] = useState("");

  /* ---------- INVITE ---------- */
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"member" | "admin">("member");
  const [invitePassword, setInvitePassword] = useState("");
  const [invitePermissions, setInvitePermissions] = useState<Permissions>({
    bacteria: true,
    microbial: true,
    snp: false,
    gwas: false,
    reports: false,
    "data-manager": true,
  });

  /* ================= HELPERS ================= */

  function avatar(email: string) {
    return email?.[0]?.toUpperCase() || "?";
  }

  async function fetchAPI<T>(path: string, options?: RequestInit): Promise<T> {
    const res = await fetch(`${API_BASE}${path}`, {
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...(options?.headers || {}),
      },
      ...options,
    });

    if (res.status === 204) return null as T;

    const text = await res.text();
    const data = text ? JSON.parse(text) : null;

    if (!res.ok) {
      throw new Error(data?.detail || data?.message || `Error ${res.status}`);
    }

    return data as T;
  }

  /* ================= API ================= */

  async function loadTeams() {
    const data = await fetchAPI<Team[]>("/api/teams");
    setTeams(data || []);
    if (!activeTeam && data?.length) setActiveTeam(data[0].id);
  }

  async function loadMembers(teamId: number) {
    const data = await fetchAPI<Member[]>(`/api/teams/${teamId}/members`);
    setMembers(data || []);
  }

  async function createTeam() {
    if (!newTeamName) return;
    setBusy(true);
    try {
      await fetchAPI("/api/teams", {
        method: "POST",
        body: JSON.stringify({ name: newTeamName }),
      });
      setNewTeamName("");
      setShowCreate(false);
      await loadTeams();
    } finally {
      setBusy(false);
    }
  }

  async function inviteMember() {
    if (!activeTeam || !inviteEmail || !invitePassword) return;
    setBusy(true);
    try {
      await fetchAPI(`/api/teams/${activeTeam}/invite`, {
        method: "POST",
        body: JSON.stringify({
          email: inviteEmail,
          role: inviteRole,
          password: invitePassword,
          permissions: invitePermissions,
        }),
      });
      setInviteEmail("");
      setInvitePassword("");
      setInviteRole("member");
      setShowInvite(false);
      await loadMembers(activeTeam);
    } finally {
      setBusy(false);
    }
  }

  async function removeMember(memberId: number) {
    if (!confirm("Remove member from team?")) return;
    if (!activeTeam) return;

    setBusy(true);
    try {
      await fetchAPI(`/api/teams/${activeTeam}/members/${memberId}`, {
        method: "DELETE",
      });
      await loadMembers(activeTeam);
    } finally {
      setBusy(false);
    }
  }

  /* ================= EFFECTS ================= */

  useEffect(() => {
    loadTeams().finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (activeTeam) loadMembers(activeTeam);
  }, [activeTeam]);

  /* ================= UI ================= */

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-white">
        <Loader2 className="animate-spin" /> Loading…
      </div>
    );
  }

  return (
    <div
      className="
        min-h-screen
        bg-gradient-to-br from-black via-indigo-900 to-purple-900
        px-6 pt-20 pb-20
        text-white
      "
    >
      {/* ===== HEADER ===== */}
      <div className="max-w-5xl mx-auto mb-10">
        <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-8 shadow-2xl">
          <h1 className="text-3xl font-extrabold flex gap-2 items-center">
            <Users /> Teams
          </h1>
          <p className="mt-2 text-indigo-200 text-sm">
            Manage collaboration, roles, and permissions across your projects.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto space-y-8">

        {/* Team Selector */}
        <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-6 shadow-xl max-w-md">
          <label className="text-xs font-semibold text-indigo-200 mb-2 block">
            Active team
          </label>
          <select
            value={activeTeam ?? ""}
            onChange={(e) => setActiveTeam(Number(e.target.value))}
            className="w-full bg-black/30 border border-white/20 rounded-xl px-3 py-2"
          >
            {teams.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <Btn icon={<Mail size={16} />} onClick={() => setShowInvite(true)}>
            Invite
          </Btn>
          <Btn icon={<Plus size={16} />} onClick={() => setShowCreate(true)}>
            New Team
          </Btn>
          <Btn icon={<Settings size={16} />} onClick={() => setShowSettings(true)}>
            Settings
          </Btn>
        </div>

        {/* Members */}
        <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl shadow-xl p-6 space-y-4">
          {members.map((m) => (
            <div
              key={m.id}
              className="flex justify-between items-center border-b border-white/10 pb-3"
            >
              <div className="flex gap-3 items-center">
                <div className="w-9 h-9 rounded-full bg-indigo-600 flex items-center justify-center font-bold">
                  {avatar(m.email)}
                </div>
                <div>
                  <div className="font-mono text-sm">{m.email}</div>
                  <div className="text-xs text-indigo-300">{m.role}</div>
                </div>
              </div>

              {m.role !== "owner" && (
                <Trash2
                  size={16}
                  className="text-rose-400 cursor-pointer"
                  onClick={() => removeMember(m.id)}
                />
              )}
            </div>
          ))}
        </div>

        {error && <div className="text-rose-400">{error}</div>}
      </div>

      {/* ===== INVITE MODAL ===== */}
      {showInvite && (
        <Modal title="Invite Member" onClose={() => setShowInvite(false)}>
          <input
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            placeholder="Email"
            className="input"
          />
          <input
            value={invitePassword}
            onChange={(e) => setInvitePassword(e.target.value)}
            placeholder="Temporary password"
            className="input"
          />
          <select
            value={inviteRole}
            onChange={(e) => setInviteRole(e.target.value as any)}
            className="input"
          >
            <option value="member">member</option>
            <option value="admin">admin</option>
          </select>

          <div className="grid grid-cols-2 gap-2 text-xs">
            {Object.keys(invitePermissions).map((k) => (
              <label key={k} className="flex gap-2 items-center">
                <input
                  type="checkbox"
                  checked={invitePermissions[k]}
                  onChange={() =>
                    setInvitePermissions((p) => ({ ...p, [k]: !p[k] }))
                  }
                />
                {k}
              </label>
            ))}
          </div>

          <ModalActions onConfirm={inviteMember} busy={busy} />
        </Modal>
      )}
    </div>
  );
}

/* ================= COMPONENTS ================= */

function Btn({
  children,
  icon,
  onClick,
}: {
  children: React.ReactNode;
  icon?: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="
        bg-gradient-to-r from-indigo-600 to-purple-600
        px-4 py-2 rounded-xl font-semibold
        flex items-center gap-2
        hover:scale-[1.02] transition
      "
    >
      {icon}
      {children}
    </button>
  );
}

function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-6 w-full max-w-md space-y-4 text-white">
        <div className="flex justify-between items-center">
          <h3 className="font-bold">{title}</h3>
          <X onClick={onClose} className="cursor-pointer" />
        </div>
        {children}
      </div>
    </div>
  );
}

function ModalActions({
  onConfirm,
  busy,
}: {
  onConfirm: () => void;
  busy: boolean;
}) {
  return (
    <div className="flex justify-end">
      <button
        onClick={onConfirm}
        disabled={busy}
        className="bg-emerald-600 px-4 py-2 rounded-xl flex items-center gap-2"
      >
        {busy && <Loader2 className="animate-spin" size={16} />}
        <Check size={16} /> Confirm
      </button>
    </div>
  );
}
